import { Button, Input, Prompt, Select, Switch, Text, toast } from "@medusajs/ui"
import { useEffect, useState } from "react"
import { Field } from "@/components/form"
import { Section } from "@/components/ui"
import { formatDate } from "@/lib/format"
import { errorMessage } from "@/lib/sdk"
import { CONTENT_ID_LABEL, useConversionsApi, useRemoveConversionsApiToken, useSaveConversionsApi, useSendConversionsApiTest, type CapiContentId, type CapiTestResult } from "@/lib/conversions-api"
import { Badge, Banner, DefinitionList } from "@/terabizz"

/**
 * Settings → Customer events → Meta Conversions API: the same events as the Meta pixel (Purchase,
 * InitiateCheckout, AddToCart, ViewContent, Lead), also sent from the server with the same event
 * ids, so Meta counts each once and still sees what ad blockers and browsers hide. The access token
 * is stored encrypted and never shown again (last 4 only). Only the owner and staff with full
 * permissions may change it or send a test event; everyone with settings access sees the status.
 */
export function ConversionsApiCard() {
  const { data, error } = useConversionsApi()
  const save = useSaveConversionsApi()
  const remove = useRemoveConversionsApiToken()
  const test = useSendConversionsApiTest()
  const s = data?.capi
  const [token, setToken] = useState("")
  const [code, setCode] = useState("")
  const [confirmRemove, setConfirmRemove] = useState(false)
  const [result, setResult] = useState<CapiTestResult | null>(null)
  useEffect(() => setCode(s?.test_event_code ?? ""), [s?.test_event_code])
  if (error) return <Section title="Meta Conversions API"><Text size="small" className="p-4 text-ui-fg-error">{errorMessage(error)}</Text></Section>
  if (!s) return null
  const manage = s.can_manage
  const tokenOk = token.trim() === "" || /^[A-Za-z0-9_\-.|]{30,600}$/.test(token.trim())
  const codeOk = /^[A-Za-z0-9_-]{0,40}$/.test(code.trim())
  const set = (body: Parameters<typeof save.mutate>[0], ok: string) => save.mutate(body, { onSuccess: () => toast.success(ok), onError: (e) => toast.error(errorMessage(e)) })
  const status = !s.pixel_id ? ["Add the pixel ID", "warning"] : !s.connected ? ["Not connected", "neutral"] : !s.readable ? ["Reconnect needed", "critical"] : s.paused_waiting > 0 ? ["Paused", "critical"] : s.enabled ? ["On", "success"] : ["Off", "neutral"]

  return (
    <Section title="Meta Conversions API" actions={<Badge tone={status[1] as any}>{status[0]}</Badge>}>
      <div className="flex flex-col gap-3 p-4">
        <Text size="small" className="text-ui-fg-subtle">
          Sends Purchase, InitiateCheckout, AddToCart, ViewContent and Lead to Meta from the server as well as from the pixel, with the same event id, so Meta counts each once. Email, phone, name and address are hashed (SHA-256) before they leave. Follows the cookie banner exactly like the pixel: with the banner on, only for visitors who accepted.
        </Text>
        {!s.pixel_id && <Banner tone="warning" title="No Meta pixel ID yet">Add it under Marketing pixels below: the Conversions API sends to the same pixel.</Banner>}
        {s.connected && !s.readable && <Banner tone="critical" title="The saved access token can't be read">The server secret changed. Paste the access token again.</Banner>}
        {s.paused_waiting > 0 && (
          <Banner tone="critical" title={`Paused: Meta refused the access token (${s.paused_waiting} event${s.paused_waiting === 1 ? "" : "s"} waiting)`}>
            The token expired, was revoked, or lost access to the pixel. Nothing is lost: paste a new token below and the waiting events are sent straight away (Meta takes them up to 7 days old).
          </Banner>
        )}
        {s.test_code_on_live && (
          <Banner tone="warning" title="Live events carry the test event code">
            Real purchases and visits are showing in Events Manager → Test events as test traffic. Switch “Add the test code to live events too” off as soon as you've finished checking.
          </Banner>
        )}
        {s.last_24h.failed > 0 && (
          <Banner tone="warning" title={`${s.last_24h.failed} event${s.last_24h.failed === 1 ? "" : "s"} couldn't be delivered in the last 24 hours`}>
            {s.last_failure ? `${s.last_failure.event_name}: ${s.last_failure.last_error ?? "failed"}` : "See Issues for Meta's answer."}
          </Banner>
        )}
      </div>
      {s.connected && (
        <DefinitionList
          rows={[
            ["Pixel", s.pixel_id ?? "—"],
            ["Access token", s.token_last4 ? `••••${s.token_last4}` : "—"],
            ["Sent in the last 24 hours", String(s.last_24h.sent)],
            ["Waiting or retrying", String(s.last_24h.pending)],
            ["Subscription renewals", s.send_renewals ? "Sent (as system events)" : "Not sent"],
            ["Content ID", CONTENT_ID_LABEL[s.content_id] ?? CONTENT_ID_LABEL.sku],
            ["Connected", s.connected_at ? formatDate(s.connected_at) : "—"],
            ...(s.last_test_at ? ([["Last test event", `${s.last_test_ok ? "Accepted" : "Refused"} · ${formatDate(s.last_test_at)}`]] as [string, string][]) : []),
          ]}
        />
      )}
      {manage ? (
        <div className="flex flex-col gap-4 border-t border-ui-border-base px-4 py-3">
          <Field label={s.connected ? "Replace the access token" : "Conversions API access token"} hint={!tokenOk ? "That doesn't look like an access token." : "Events Manager → your pixel → Settings → Conversions API → Generate access token. Stored encrypted; never shown again."}>
            <Input type="password" autoComplete="off" value={token} placeholder={s.token_last4 ? `••••${s.token_last4}` : "EAA…"} aria-invalid={!tokenOk} maxLength={600} onChange={(e) => setToken(e.target.value)} />
          </Field>
          <Field label="Test event code" hint={codeOk ? "From Events Manager → Test events (e.g. TEST12345). Used by “Send test event”." : "Letters and digits only."}>
            <Input value={code} maxLength={40} aria-invalid={!codeOk} placeholder="TEST12345" onChange={(e) => setCode(e.target.value.trim())} />
          </Field>
          <label className="flex items-start gap-2 text-[13px]">
            <Switch checked={s.test_code_on_live} disabled={save.isPending || !s.test_event_code} onCheckedChange={(v) => set({ test_code_on_live: v }, v ? "Live events now carry the test code" : "Live events no longer carry the test code")} />
            <span>
              Add the test code to live events too
              <Text size="xsmall" className="text-ui-fg-subtle">Shows real storefront events in Test events while you check deduplication. Switch it off afterwards.</Text>
            </span>
          </label>
          <label className="flex items-start gap-2 text-[13px]">
            <Switch checked={s.send_renewals} disabled={save.isPending} onCheckedChange={(v) => set({ send_renewals: v }, v ? "Renewals are sent to Meta" : "Renewals aren't sent to Meta")} />
            <span>
              Send subscription renewals as Purchases
              <Text size="xsmall" className="text-ui-fg-subtle">Off by default: renewals aren't caused by an ad. When on, they go as system events, only for subscribers who accepted cookies on their first order.</Text>
            </span>
          </label>
          <Field label="Content ID" hint="The id sent to Meta as content_ids, by the pixel and the Conversions API alike. Pick the one your Meta catalog uses for its items. A product without a SKU sends its variant ID.">
            <Select
              value={s.content_id ?? "sku"}
              disabled={save.isPending}
              onValueChange={(v) => set({ content_id: v as CapiContentId }, `Meta now receives the ${CONTENT_ID_LABEL[v as CapiContentId]} as the content ID`)}
            >
              <Select.Trigger aria-label="Content ID"><Select.Value /></Select.Trigger>
              <Select.Content>
                {(Object.keys(CONTENT_ID_LABEL) as CapiContentId[]).map((k) => <Select.Item key={k} value={k}>{CONTENT_ID_LABEL[k]}</Select.Item>)}
              </Select.Content>
            </Select>
          </Field>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <label className="flex items-center gap-2 text-[13px]">
              <Switch checked={s.enabled} disabled={save.isPending || !s.connected} onCheckedChange={(v) => set({ enabled: v }, v ? "Conversions API switched on" : "Conversions API switched off")} />
              Send server events
            </label>
            <div className="flex flex-wrap gap-2">
              <Button
                size="small"
                disabled={!tokenOk || !codeOk || (token.trim() === "" && code.trim() === (s.test_event_code ?? ""))}
                isLoading={save.isPending}
                onClick={() =>
                  save.mutate(
                    { ...(token.trim() ? { access_token: token.trim() } : {}), test_event_code: code.trim() || null },
                    { onSuccess: () => (setToken(""), toast.success("Conversions API saved")), onError: (e) => toast.error(errorMessage(e)) },
                  )
                }
              >
                Save
              </Button>
              <Button
                size="small"
                variant="secondary"
                disabled={!s.active}
                isLoading={test.isPending}
                onClick={() =>
                  test.mutate(
                    { test_event_code: code.trim() || null },
                    { onSuccess: (r) => (setResult(r.test), r.test.ok ? toast.success("Meta accepted the test event") : toast.error(r.test.message ?? "Meta refused the test event")), onError: (e) => toast.error(errorMessage(e)) },
                  )
                }
              >
                Send test event
              </Button>
              {s.connected && <Button size="small" variant="danger" isLoading={remove.isPending} onClick={() => setConfirmRemove(true)}>Remove token</Button>}
            </div>
          </div>
          {result && (
            <div className="flex flex-col gap-1">
              <Text size="xsmall" weight="plus">{result.ok ? "Meta accepted it" : `Meta refused it (${result.status || "no answer"})`}{result.test_event_code ? ` · test code ${result.test_event_code}` : " · no test code (it won't show in Test events)"}</Text>
              <pre className="max-h-48 overflow-auto rounded-md bg-ui-bg-subtle p-2 text-[11px] leading-4" aria-label="Meta's response">{JSON.stringify(result.response, null, 2)}</pre>
            </div>
          )}
        </div>
      ) : (
        <Text size="xsmall" className="px-4 pb-4 text-ui-fg-subtle">Only the store owner and staff with full permissions can change the Conversions API or send a test event.</Text>
      )}
      <Prompt open={confirmRemove} onOpenChange={setConfirmRemove}>
        <Prompt.Content>
          <Prompt.Header>
            <Prompt.Title>Remove the access token?</Prompt.Title>
            <Prompt.Description>Server events stop; anything still waiting is kept and sent once a new token is pasted (Meta takes events up to 7 days old). The pixel keeps working.</Prompt.Description>
          </Prompt.Header>
          <Prompt.Footer>
            <Prompt.Cancel>Keep it</Prompt.Cancel>
            <Prompt.Action onClick={() => remove.mutate(undefined, { onSuccess: () => toast.success("Access token removed"), onError: (e) => toast.error(errorMessage(e)) })}>Remove</Prompt.Action>
          </Prompt.Footer>
        </Prompt.Content>
      </Prompt>
    </Section>
  )
}
